import { AddCircleOutlineOutlined } from "@mui/icons-material";
import { Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { ContainedRoundedButton } from "../../../styled/buttons/SecondaryRoundedButton";
import { SearchField } from "../../../styled/input-fields/SearchField";
import Svg, { SvgEnum } from "../../../ui library/components/svgs/standard/svg";
import HNSColors from "../../../ui library/HNSColors";
import DrawerFilters, { DrawerFiltersList, DrawerFilterChips } from "../../drawer-filters";
import { useActionableItemsPage } from "../hooks/useActionableItemsPage";
import { useActionItemPermissions } from "../hooks/useActionItemPermissions";

import ActionItemModals from "./ActionItemModals";
import ActionItemsTable from "./ActionItemsTable";

const ACTION_ITEMS_QUERY_OPTIONS = { debounceMs: 0, replaceNavigation: true };

export default function ActionableItems({ caseId = null, caseObj = null, title = null }) {
  const {
    filterState,
    activeFilters,
    search,
    actionItems,
    loading,
    resultCount,
    ordering,
    isActionItemModalOpen,
    setIsActionItemModalOpen,
    deleteOpen,
    setDeleteOpen,
    detailOpen,
    setDetailOpen,
    activeActionItem,
    setActiveActionItem,
    openSuccess,
    setOpenSuccess,
    openError,
    setOpenError,
    errorMessage,
    handleOrderingChange,
    handleSearchChange,
    handleEdit,
    handleDelete,
    handleView,
    handleCreate,
    handleToggleStatus,
    handleNavigateToClient,
    handleModalSuccess,
  } = useActionableItemsPage({ caseId });

  const { canAddActionItems } = useActionItemPermissions();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className="relative flex h-full w-full min-w-min flex-row">
        <div className="flex flex-1 flex-col overflow-hidden px-4">
          {title && (
            <div className="mb-4">
              <Typography variant="h5" fontWeight={600} style={{ color: HNSColors.primary[400] }}>
                {title}
              </Typography>
            </div>
          )}

          <div
            className={`flex w-full items-center justify-between ${title ? "mb-4" : "mb-4 mt-4"}`}
          >
            <div className="flex flex-1 gap-2">
              <div className="inline-flex h-[40px] min-w-[250px] max-w-[400px] flex-1 items-center rounded-full border border-gray-400 py-1 pl-3">
                <Svg name={SvgEnum.HisMagnifyingGlass} />
                <SearchField
                  placeholder="Search action items"
                  size="small"
                  name="action-item-search"
                  value={search}
                  onChange={handleSearchChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </div>

              <div className="h-[40px]">
                <DrawerFilters
                  title="Filter"
                  triggerId="actionable-items-filters-button"
                  closeButtonId="actionable-items-filters-close-button"
                >
                  <DrawerFiltersList
                    filterState={filterState}
                    options={ACTION_ITEMS_QUERY_OPTIONS}
                  />
                </DrawerFilters>
              </div>
            </div>

            <div className="h-[40px]">
              {canAddActionItems && (
                <ContainedRoundedButton
                  id="actionable-items-add-button"
                  onClick={handleCreate}
                  endIcon={<AddCircleOutlineOutlined />}
                  sx={{ height: "100%" }}
                >
                  Add Action Item
                </ContainedRoundedButton>
              )}
            </div>
          </div>

          <DrawerFilterChips
            activeFilters={activeFilters}
            queryParamsOptions={ACTION_ITEMS_QUERY_OPTIONS}
          />

          <div className="flex-1 overflow-hidden">
            <ActionItemsTable
              actionItems={actionItems}
              loading={loading}
              resultCount={resultCount}
              ordering={ordering}
              onOrderingChange={handleOrderingChange}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onView={handleView}
              onToggleStatus={handleToggleStatus}
              onNavigateToClient={handleNavigateToClient}
              caseId={caseId}
            />
          </div>
        </div>
      </div>

      <ActionItemModals
        isActionItemModalOpen={isActionItemModalOpen}
        setIsActionItemModalOpen={setIsActionItemModalOpen}
        activeActionItem={activeActionItem}
        setActiveActionItem={setActiveActionItem}
        caseObj={caseObj}
        onSuccess={handleModalSuccess}
        deleteOpen={deleteOpen}
        setDeleteOpen={setDeleteOpen}
        detailOpen={detailOpen}
        setDetailOpen={setDetailOpen}
        onNavigateToClient={handleNavigateToClient}
        openSuccess={openSuccess}
        setOpenSuccess={setOpenSuccess}
        openError={openError}
        setOpenError={setOpenError}
        errorMessage={errorMessage}
      />
    </LocalizationProvider>
  );
}
